import { AdPlacement, ObjectType, Platform, SraFormat, SraPlacement } from '@/@types/graphql-types';
import type { SponsoredAdFragment } from '@/features/sponsored-recommendations/graphql/fragments/SponsoredAd.fragment';
import type { AssertByTypename } from '@/helpers/graphql-model-helper';
import type { MaybeRefOrGetter } from '@vueuse/core';

import { computed, onServerPrefetch, provide, ref, watch } from 'vue';
import { toValue } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { useRoute } from 'vue-router/composables';

import { useMutation } from '@/helpers/composables/useApollo';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useSponsoredRecommendationsStore } from '@/stores/pinia/useSponsoredRecommendationsStore';
import { useTrackingStore } from '@/stores/pinia/useTrackingStore';

import { captureMessageForSentry } from '@/helpers/sentry-helper';
import { getSrDebuggerInfo } from '@/features/sponsored-recommendations/helpers/sponsoredrec-debugging-tools';

import {
	GetSrPlacementDocument,
	GetSrPlacementMutation,
	GetSrPlacementMutationVariables,
} from '@/features/sponsored-recommendations/graphql/mutations/GetSrPlacement.mutation';

import { SR_PROVIDER_KEY, type SRCatalogueKey, type SRPageTypes, useSponsoredRec } from './useSendBidId';

type CreativeSRContentAd = AssertByTypename<
	NonNullable<GetSrPlacementMutation['getAd']['ads']>[number],
	'CreativeSRContent'
>;

interface UseGetSrPlacementOptions {
	/** Transitional for useSendBidId - used to build the SR catalogue key */
	pageType: SRPageTypes;
	/** Transitional for useSendBidId - used to build the SR catalogue key */
	placement: SraPlacement;
	/** The AdPlacement sent to the (new) getAd mutation (e.g. AdPlacement.PopularViewSr). */
	adPlacement: AdPlacement;
	/** Seo Flag for SQTV SRs */
	isSeo?: boolean;
	alwaysReturnBidID?: boolean;
	jwEntityID?: MaybeRefOrGetter<string | undefined>;
	isEnabled?: MaybeRefOrGetter<boolean>;
}

const supportedFormats = [SraFormat.Image, SraFormat.Video];
const supportedObjectTypes = [ObjectType.Movie, ObjectType.Show, ObjectType.GenericTitleList, ObjectType.ShowSeason];

/** Maps CreativeSRContent response to SponsoredAdFragment structure for compatibility with useSendBidId. */
function mapToSponsoredAd(ad: CreativeSRContentAd): SponsoredAdFragment {
	return {
		__typename: 'SponsoredRecommendationAd',
		bidId: ad.bidID,
		holdoutGroup: ad.holdoutGroup,
		campaign: ad.campaign,
	};
}

/** Type guard utility for CreativeSRContent. */
function isCreativeSRContent(ad: { __typename?: string }): ad is CreativeSRContentAd {
	return ad.__typename === 'CreativeSRContent';
}

/** Standalone logic to get a Sponsored Recommendation via the getAd mutation. */
export function useGetSrPlacement({
	pageType,
	placement,
	adPlacement,
	jwEntityID,
	alwaysReturnBidID = true,
	isEnabled = true,
	isSeo = false,
}: UseGetSrPlacementOptions) {
	const route = useRoute();
	const trackingStore = useTrackingStore();
	const { language, country } = storeToRefs(useLanguageStore());
	const srStore = useSponsoredRecommendationsStore();
	const { setSponsoredAd } = srStore;

	const isSRLoaded = ref(false);
	const mutationPromise = ref<Promise<any> | null>(null);

	const srDebuggerInfo = getSrDebuggerInfo();

	// Set the key (same format as useSendBidId)
	const srCatalogueKey: SRCatalogueKey = `${placement}-${pageType}`;
	provide(SR_PROVIDER_KEY, srCatalogueKey);

	// Manage Mutation Request
	const { onDone, onError, mutate } = useMutation<GetSrPlacementMutation, GetSrPlacementMutationVariables>(
		GetSrPlacementDocument,
		() => ({
			variables: {
				input: {
					limit: 1,
					placements: [adPlacement],
					country: country.value,
					language: language.value,
					platform: Platform.Web,
					url: route.fullPath,
					jwEntityID: toValue(jwEntityID) ?? undefined,
					isSeo,
					alwaysReturnBidID,
					supportedFormats,
					supportedObjectTypes,
					testing: srDebuggerInfo
						? {
								debug: false,
								dryRun: false,
								srTestingMode: srDebuggerInfo.testingMode,
								srTestingModeCampaignName: srDebuggerInfo.campaignName,
								srTestingModeSubcampaignId: srDebuggerInfo.subcampaignId,
								srTestingModeAdID: srDebuggerInfo.adID,
								srTestingModeForceHoldoutGroup: !!srDebuggerInfo.holdoutGroup,
						  }
						: undefined,
				},
				language: language.value,
				country: country.value,
			},
			fetchPolicy: 'no-cache',
		})
	);

	onDone(({ data }) => {
		const srAd = data?.getAd.ads?.find(isCreativeSRContent);

		if (srCatalogueKey != null && srAd != null) {
			setSponsoredAd({ catalogueKey: srCatalogueKey, ad: mapToSponsoredAd(srAd) });
		}
		isSRLoaded.value = true;
	});

	onError(error => {
		captureMessageForSentry(
			'[GraphQL GetSrPlacement]:',
			{ error, where: 'Composable: useGetSrPlacement', placement: adPlacement },
			'error',
			{ SR: srCatalogueKey }
		);

		// Mark as loaded to allow SSR to continue gracefully without ad
		isSRLoaded.value = true;
	});

	watch(
		[
			// needed otherwise all values in there would still be null
			() => trackingStore.sessionEnvironment.sessionReady,
			() => toValue(isEnabled),
			() => toValue(jwEntityID),
		],

		([isReady, isEnabled, hasJwEntity]) => {
			const placementMutationConditionFulfilled =
				placement === SraPlacement.DetailPage ? isEnabled && hasJwEntity : isEnabled;

			// On server: only run for SEO placements. On client: never run if isSeo=true
			const shouldMutate = process.server
				? isSeo && placementMutationConditionFulfilled
				: !isSeo && isReady && placementMutationConditionFulfilled;

			if (shouldMutate) {
				mutationPromise.value = mutate().catch(() => {});
			}
		},
		{ immediate: true }
	);

	onServerPrefetch(async () => {
		if (!isSeo) return;

		if (mutationPromise.value) {
			await mutationPromise.value;
		}
	});

	const sponsoredAd = computed(() => srStore.catalogue[srCatalogueKey] ?? null);

	return {
		isSRLoaded,
		sponsoredAd,
		...useSponsoredRec({ sponsoredAd }),
	};
}
